/**
 * Per-customer clinical detail screen. Full-page route
 * (`/admin/clinical-admin/customers/:patientId`) with its own tabset —
 * Overview, EMR, Prescriptions, Orders & Pharmacy, Billing, Intake,
 * Documents. Active tab is held in the URL (`?tab=`) so it's shareable.
 */
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  useClinicalPatientSummary,
  useGeneratePatientLoginLink,
  useMembershipLink,
} from '@/lib/api/clinicalAdminHooks';

import {
  BillingTab,
  DocumentsTab,
  EmrTab,
  IntakeTab,
  OrdersTab,
  OverviewTab,
  PrescriptionsTab,
} from './CustomerTabs';
import { useDevToolsAvailable } from '@/lib/api/devToolsHooks';

import { CheckinDoseHistoryTab } from './CheckinDoseHistoryTab';
import { DevToolsTab } from './DevToolsTab';
import { ClinicalAgentDrawer } from './ClinicalAgentDrawer';
import { Badge, ErrorState, LoadingState, isTestEmail } from './ui';
import { TicketDrawer } from '../support/TicketDrawer';

type TabKey =
  | 'overview'
  | 'emr'
  | 'prescriptions'
  | 'checkins'
  | 'orders'
  | 'billing'
  | 'intake'
  | 'documents'
  | 'dev-tools';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'emr', label: 'EMR' },
  { key: 'prescriptions', label: 'Prescriptions' },
  { key: 'checkins', label: 'Check-ins & dose changes' },
  { key: 'orders', label: 'Orders & Pharmacy' },
  { key: 'billing', label: 'Billing' },
  { key: 'intake', label: 'Intake' },
  { key: 'documents', label: 'Documents' },
];

// Appended only when the backend serves /admin/dev-tools/capabilities. In
// production those routes are not mounted at all, so the probe 404s and the
// tab never appears — the server decides, not a VITE_* build var, so a stale
// frontend cannot offer a surface the backend doesn't have.
const DEV_TOOLS_TAB: { key: TabKey; label: string } = {
  key: 'dev-tools',
  label: 'Dev tools',
};

export function ClinicalCustomerScreen() {
  const { patientId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { data: summary, isLoading, isError } =
    useClinicalPatientSummary(patientId);
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [agentOpen, setAgentOpen] = useState(false);

  const devToolsAvailable = useDevToolsAvailable();
  const tabs = devToolsAvailable ? [...TABS, DEV_TOOLS_TAB] : TABS;

  const rawTab = params.get('tab') as TabKey | null;
  const tab: TabKey =
    rawTab && tabs.some((t) => t.key === rawTab) ? rawTab : 'overview';

  const selectTab = (key: TabKey) => {
    const next = new URLSearchParams(params);
    if (key === 'overview') next.delete('tab');
    else next.set('tab', key);
    setParams(next, { replace: true });
  };

  return (
    <div className="flex flex-col gap-24">
      <div className="flex flex-col gap-8">
        <Link
          to="/admin/clinical-admin?tab=crm"
          className="type-label-sm text-ink-muted hover:text-ink w-fit"
        >
          ← Back to CRM
        </Link>
        {isLoading && <LoadingState label="Loading customer…" />}
        {isError && (
          <ErrorState>Customer not found or could not be loaded.</ErrorState>
        )}
        {summary && (
          <div className="flex flex-wrap items-center gap-12">
            <h1 className="type-heading-xl">
              {summary.display_name || '(no name)'}
            </h1>
            <span className="type-body-md text-ink-muted">
              {summary.email || '—'}
            </span>
            {isTestEmail(summary.email) && <Badge tone="warn">TEST</Badge>}
            {summary.derived_status && (
              <Badge tone="accent">
                {summary.derived_status.replace(/_/g, ' ')}
                {summary.pharmacy_stage
                  ? ` (${summary.pharmacy_stage.toLowerCase()})`
                  : ''}
              </Badge>
            )}
            {(summary.open_warnings ?? 0) > 0 && (
              <Badge tone="danger">{summary.open_warnings} open warnings</Badge>
            )}
            <button
              type="button"
              onClick={() => setAgentOpen(true)}
              className="type-label-sm px-10 py-4 rounded border border-accent text-accent bg-accent-wash"
            >
              Clinical AI
            </button>
            <ViewAsPatient patientId={patientId} />
            <MembershipLink patientId={patientId} />
          </div>
        )}
      </div>

      {summary && (
        <>
          <div className="flex items-center gap-2 border-b border-line overflow-x-auto">
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`type-label-sm whitespace-nowrap px-12 py-10 -mb-px border-b-2 ${
                  tab === t.key
                    ? 'border-ink text-ink'
                    : 'border-transparent text-ink-muted hover:text-ink'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'overview' && (
            <OverviewTab summary={summary} onOpenTicket={setOpenTicketId} />
          )}
          {tab === 'emr' && <EmrTab patientId={patientId} />}
          {tab === 'prescriptions' && (
            <PrescriptionsTab patientId={patientId} />
          )}
          {tab === 'checkins' && <CheckinDoseHistoryTab patientId={patientId} />}
          {tab === 'orders' && <OrdersTab patientId={patientId} />}
          {tab === 'billing' && <BillingTab patientId={patientId} />}
          {tab === 'intake' && <IntakeTab patientId={patientId} />}
          {tab === 'documents' && <DocumentsTab patientId={patientId} />}
          {tab === 'dev-tools' && devToolsAvailable && (
            <DevToolsTab patientId={patientId} />
          )}
        </>
      )}

      <TicketDrawer
        ticketId={openTicketId}
        onClose={() => setOpenTicketId(null)}
      />
      <ClinicalAgentDrawer
        open={agentOpen}
        onClose={() => setAgentOpen(false)}
        context={{
          patientName: summary?.display_name,
          patientEmail: summary?.email,
        }}
      />
    </div>
  );
}

/** "View as patient" — mints a one-time login link for this patient so staff
 *  can see exactly what the patient sees. Impersonation, so it's admin-gated +
 *  audit-logged server-side; open the link in an incognito window so it
 *  doesn't clobber the admin session. The patient is NOT emailed. */
/** A rep's System Membership link to send the patient after a call. */
function MembershipLink({ patientId }: { patientId: string }) {
  const mint = useMembershipLink(patientId);
  const [copied, setCopied] = useState(false);
  const url = mint.data?.url ?? null;

  const create = async () => {
    const result = await mint.mutateAsync().catch(() => null);
    if (!result) return;
    try {
      await navigator.clipboard.writeText(result.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is still selectable below */
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <button
        type="button"
        onClick={() => void create()}
        disabled={mint.isPending}
        className="self-start type-label-sm px-10 py-4 rounded border border-line bg-raised hover:bg-sunken disabled:opacity-40 whitespace-nowrap"
        title="Copy a System Membership link for this patient ($199/yr, 50% off one renewal). Recorded as your enrollment when they join."
      >
        {mint.isPending ? 'Creating…' : copied ? 'Membership link copied' : 'Membership link'}
      </button>
      {mint.isError && (
        <span className="type-10 text-danger">
          {(mint.error as Error)?.message || 'Could not create a membership link.'}
        </span>
      )}
      {url && (
        <div className="type-10 text-ink-muted break-all max-w-[40rem]">
          Send this to the patient (valid {mint.data?.expires_days} days, only for them). It
          offers the $199/yr membership and records the enrollment as yours.
          <div className="font-mono mt-2">{url}</div>
        </div>
      )}
    </div>
  );
}

function ViewAsPatient({ patientId }: { patientId: string }) {
  const mint = useGeneratePatientLoginLink(patientId);
  const [copied, setCopied] = useState(false);

  const link = mint.data?.action_link ?? null;

  const copy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the link is still selectable below */
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="flex items-center gap-8">
        <button
          type="button"
          onClick={() => mint.mutate()}
          disabled={mint.isPending}
          className="type-label-sm px-10 py-4 rounded border border-line bg-raised hover:bg-sunken disabled:opacity-40 whitespace-nowrap"
          title="Mint a one-time sign-in link to view the portal as this patient (opens in a new tab; the patient is not emailed)."
        >
          {mint.isPending ? 'Generating…' : 'View as patient'}
        </button>
        {link && (
          <>
            <a
              href={link}
              target="_blank"
              rel="noreferrer"
              className="type-label-sm text-accent hover:underline"
            >
              Open (incognito) →
            </a>
            <button
              type="button"
              onClick={copy}
              className="type-label-sm text-ink-muted hover:text-ink"
            >
              {copied ? 'Copied' : 'Copy link'}
            </button>
          </>
        )}
      </div>
      {mint.isError && (
        <span className="type-10 text-danger">
          {(mint.error as Error)?.message || 'Could not generate a login link.'}
        </span>
      )}
      {link && (
        <div className="type-10 text-ink-muted break-all max-w-[40rem]">
          Single-use, expires ~1h. Open in an incognito window so it doesn’t
          sign you out of admin.
          {mint.data?.otp && <> · Code: <span className="font-mono">{mint.data.otp}</span></>}
        </div>
      )}
    </div>
  );
}
